import { useState } from "react";
import { 
  Button, 
  Flex, 
  Input, 
  Select, 
  Space, 
  Table, 
  Typography, 
  Modal, 
  Form, 
  message, 
  Tooltip, 
  Tag,
  Descriptions,
  DatePicker,
  Row,
  Col,
  Card
} from "antd";
import { ReloadOutlined, EyeOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import RobinPage from "../components/RobinPage";
import { gql, useQuery, useMutation } from "@apollo/client";

// Note: These queries are defined in src/graphql/driver.graphql
// After running codegen, use the generated hooks:
// - useAllDriversListQuery
// - useAllWithdrawalsQuery  
// - useUpdateWithdrawalStatusMutation
// from "../graphql/generated/hooks"

const ALL_DRIVERS_LIST = gql`
  query AllDriversList {
    allDriversList {
      id
      name
    }
  }
`;

const ALL_WITHDRAWALS = gql`
  query AllWithdrawals(
    $pagination: PaginationInput
    $filter: WithdrawalFilterInput
  ) {
    allWithdrawals(pagination: $pagination, filter: $filter) {
      withdrawals {
        id
        amountMinor
        status
        description
        rejectionReason
        balanceAtTime
        createdAt
        updatedAt
        driver {
          id
          name
          phoneNumber
          email
        }
        beneficiary {
          id
          name
          iban
          bank
          bankCode
        }
      }
      total
      hasMore
      limit
      offset
    }
  }
`;

const UPDATE_WITHDRAWAL_STATUS = gql`
  mutation UpdateWithdrawalStatus($input: UpdateWithdrawalStatusDto!) {
    updateWithdrawalStatus(input: $input) {
      id
      amountMinor
      status
      driver {
        id
        name
      }
      beneficiary {
        name
        iban
      }
      updatedAt
    }
  }
`;

const { Text } = Typography;
const { RangePicker } = DatePicker;

type WithdrawalStatus = "PENDING" | "APPROVED" | "REJECTED" | "PROCESSING" | "COMPLETED" | "FAILED";

const WITHDRAWAL_STATUS_OPTIONS: WithdrawalStatus[] = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "PROCESSING",
  "COMPLETED",
  "FAILED",
];

// Type definitions (will be replaced by generated types after codegen runs)
type Driver = {
  id?: number | null;
  name?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
} | null;

type Beneficiary = {
  id?: number | null;
  name?: string | null;
  iban?: string | null;
  bank?: string | null;
  bankCode?: string | null;
} | null;

type Withdrawal = {
  id?: number | null;
  amountMinor?: number | null;
  status?: string | null;
  description?: string | null;
  rejectionReason?: string | null;
  balanceAtTime?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  driver?: Driver;
  beneficiary?: Beneficiary;
} | null;

type AllWithdrawalsResponse = {
  allWithdrawals?: {
    withdrawals?: Withdrawal[] | null;
    total?: number | null;
    hasMore?: boolean | null;
    limit?: number | null;
    offset?: number | null;
  } | null;
};

type DriverListItem = {
  id?: number | null;
  name?: string | null;
} | null;

type AllDriversListResponse = {
  allDriversList?: DriverListItem[] | null;
};

function WithdrawalsList() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  // Input state (what user types)
  const [statusInput, setStatusInput] = useState<WithdrawalStatus[]>([]);
  const [driverIdInput, setDriverIdInput] = useState<number | undefined>(undefined);
  const [ibanInput, setIbanInput] = useState<string>("");
  const [dateRangeInput, setDateRangeInput] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  
  // Applied filter state (what is sent to query)
  const [statusFilter, setStatusFilter] = useState<WithdrawalStatus[]>([]);
  const [driverIdFilter, setDriverIdFilter] = useState<number | undefined>(undefined);
  const [ibanFilter, setIbanFilter] = useState<string>("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Modal state
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);

  const { data: driversData } = useQuery<AllDriversListResponse>(ALL_DRIVERS_LIST);

  const { data, loading, error, refetch } = useQuery<AllWithdrawalsResponse>(
    ALL_WITHDRAWALS,
    {
      variables: {
        pagination: {
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
        },
        filter: {
          driverId: driverIdFilter || null,
          iban: ibanFilter || null,
          statuses: statusFilter.length > 0 ? statusFilter : undefined,
          createdAfter: dateRange?.[0] ? dateRange[0].toISOString() : null,
          createdBefore: dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : null,
        },
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const [updateWithdrawalStatus, { loading: updateLoading }] = useMutation(
    UPDATE_WITHDRAWAL_STATUS,
    {
      onCompleted: () => {
        message.success(t("message.success.data_updated"));
        setIsStatusModalVisible(false);
        form.resetFields();
        setSelectedWithdrawal(null);
        refetch();
      },
      onError: (error: Error) => {
        message.error(error.message || t("message.error.data_update_failed"));
      },
    }
  );

  const handleOpenViewModal = (withdrawal: Withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setIsViewModalVisible(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalVisible(false);
    setSelectedWithdrawal(null);
  };

  const handleOpenStatusModal = (withdrawal: Withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setIsStatusModalVisible(true);
    form.resetFields();
  };

  const handleCloseStatusModal = () => {
    setIsStatusModalVisible(false);
    setSelectedWithdrawal(null);
    form.resetFields();
  };

  const handleSubmitStatus = async (values: any) => {
    if (!selectedWithdrawal?.id) return;

    await updateWithdrawalStatus({
      variables: {
        input: {
          withdrawalId: selectedWithdrawal.id,
          status: values.status,
        },
      },
    });
  };

  const handleApplyFilters = () => {
    setStatusFilter(statusInput);
    setDriverIdFilter(driverIdInput);
    setIbanFilter(ibanInput);
    setDateRange(dateRangeInput);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setStatusInput([]);
    setDriverIdInput(undefined);
    setIbanInput("");
    setDateRangeInput(null);
    setStatusFilter([]);
    setDriverIdFilter(undefined);
    setIbanFilter("");
    setDateRange(null);
    setCurrentPage(1);
  };

  const getStatusColor = (status?: string | null) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "success";
      case "PENDING":
        return "warning";
      case "REJECTED":
        return "error";
      case "PROCESSING":
        return "processing";
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "-";
    try {
      return dayjs(dateString).format("DD/MM/YYYY HH:mm");
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amountMinor?: number | null) => {
    if (amountMinor === null || amountMinor === undefined) return "-";
    // amountMinor is already in SAR, not in minor units
    return parseFloat(String(amountMinor)).toFixed(2) + " SAR";
  };

  const withdrawals: Withdrawal[] = data?.allWithdrawals?.withdrawals ?? [];
  const total = data?.allWithdrawals?.total ?? 0;

  const dataSource = withdrawals.map((withdrawal: Withdrawal) => ({
    key: withdrawal?.id ?? 0,
    id: withdrawal?.id ?? "-",
    amount: formatAmount(withdrawal?.amountMinor),
    status: withdrawal?.status ?? "-",
    driverName: withdrawal?.driver?.name ?? "-",
    bank: withdrawal?.beneficiary?.bank ?? "-",
    iban: withdrawal?.beneficiary?.iban ?? "-",
    createdAt: formatDate(withdrawal?.createdAt),
    withdrawal: withdrawal,
  }));

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: t("label.withdrawal.amount"),
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: t("label.status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        if (!status || status === "-") return "-";
        const statusStr = String(status);
        return (
          <Tag color={getStatusColor(statusStr)}>
            {t(`label.withdrawal.status.${statusStr.toLowerCase()}`, { defaultValue: statusStr })}
          </Tag>
        );
      },
    },
    {
      title: t("label.driver.name"),
      dataIndex: "driverName",
      key: "driverName",
    },
    {
      title: t("label.beneficiary.bank"),
      dataIndex: "bank",
      key: "bank",
    },
    {
      title: t("label.withdrawal.created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
    },
    {
      title: t("label.actions"),
      key: "actions",
      width: 120,
      align: "center" as const,
      render: (_: any, record: any) => (
        <Space size="small">
          <Tooltip title={t("button.view")}>
            <Button
              type="primary"
              shape="circle"
              icon={<EyeOutlined />}
              onClick={() => handleOpenViewModal(record.withdrawal)}
            />
          </Tooltip>
          <Tooltip title={t("button.change_status")}>
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => handleOpenStatusModal(record.withdrawal)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <RobinPage
      title={t("label.menu.withdraw_requests")}
      actions={
        <Space size="middle">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            disabled={loading}
          >
            {t("button.refresh")}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Flex gap="middle" wrap="wrap" style={{ width: "100%" }}>
          <Select
            showSearch
            allowClear
            value={driverIdInput}
            placeholder={t("label.withdrawal.driver_id")}
            onChange={(value) => setDriverIdInput(value)}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={(driversData?.allDriversList || [])
              .filter((driver: DriverListItem): driver is NonNullable<DriverListItem> => 
                driver !== null && driver !== undefined && driver.id !== null && driver.id !== undefined
              )
              .map((driver: NonNullable<DriverListItem>) => ({
                value: driver.id!,
                label: `${driver.id} - ${driver.name || "-"}`,
              }))}
            style={{ flex: "1 1 calc(25% - 8px)", minWidth: 150 }}
          />
          <Input
            allowClear
            value={ibanInput}
            placeholder={t("label.beneficiary.iban")}
            onChange={(e) => setIbanInput(e.target.value)}
            onPressEnter={handleApplyFilters}
            style={{ flex: "1 1 calc(25% - 8px)", minWidth: 150 }}
          />
          <Select
            mode="multiple"
            allowClear
            value={statusInput}
            placeholder={t("label.status")}
            options={WITHDRAWAL_STATUS_OPTIONS.map((status) => ({
              value: status,
              label: t(`label.withdrawal.status.${status.toLowerCase()}`, { defaultValue: status }),
            }))}
            onChange={(value) => setStatusInput(value as WithdrawalStatus[])}
            style={{ flex: "1 1 calc(25% - 8px)", minWidth: 150 }}
          />
          <RangePicker
            value={dateRangeInput}
            onChange={(dates) => setDateRangeInput(dates as [Dayjs | null, Dayjs | null] | null)}
            style={{ flex: "1 1 calc(25% - 8px)", minWidth: 250 }}
            format="DD/MM/YYYY"
          />
          <Space.Compact style={{ flex: "1 1 calc(25% - 8px)", minWidth: 200 }}>
            <Button
              type="primary"
              onClick={handleApplyFilters}
              style={{ flex: 1 }}
            >
              {t("button.search")}
            </Button>
            <Button
              onClick={handleResetFilters}
              style={{ flex: 1 }}
            >
              {t("button.reset_filters")}
            </Button>
          </Space.Compact>
        </Flex>
      </Space>
      {error ? (
        <Typography.Text type="danger">
          {t("error.failed_to_load")}
        </Typography.Text>
      ) : null}
      <Table 
        loading={loading} 
        dataSource={dataSource} 
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => t("label.total_items", { total }),
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      {/* View Withdrawal Modal */}
      <Modal
        title={t("label.withdrawal.details")}
        open={isViewModalVisible}
        onCancel={handleCloseViewModal}
        footer={[
          <Button key="close" onClick={handleCloseViewModal}>
            {t("button.close")}
          </Button>,
        ]}
        width={800}
        destroyOnHidden
      >
        {selectedWithdrawal && (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {/* Withdrawal Information */}
            <Card title={t("label.withdrawal.information")}>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label={t("label.withdrawal.id")}>
                  {selectedWithdrawal.id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("label.withdrawal.amount")}>
                  {formatAmount(selectedWithdrawal.amountMinor)}
                </Descriptions.Item>
                <Descriptions.Item label={t("label.status")}>
                  <Tag color={getStatusColor(selectedWithdrawal.status)}>
                    {selectedWithdrawal.status 
                      ? t(`label.withdrawal.status.${selectedWithdrawal.status.toLowerCase()}`, { defaultValue: selectedWithdrawal.status })
                      : "-"
                    }
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t("label.withdrawal.balance_at_time")}>
                  {formatAmount(selectedWithdrawal.balanceAtTime)}
                </Descriptions.Item>
                <Descriptions.Item label={t("label.withdrawal.description")} span={2}>
                  {selectedWithdrawal.description || "-"}
                </Descriptions.Item>
                {selectedWithdrawal.rejectionReason && (
                  <Descriptions.Item label={t("label.withdrawal.rejection_reason")} span={2}>
                    <Text type="danger">{selectedWithdrawal.rejectionReason}</Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t("label.created_at")}>
                  {formatDate(selectedWithdrawal.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label={t("label.updated_at")}>
                  {formatDate(selectedWithdrawal.updatedAt)}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Driver Information */}
            {selectedWithdrawal.driver && (
              <Card title={t("label.driver.info")}>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label={t("label.driver.id")}>
                    {selectedWithdrawal.driver.id || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.name")}>
                    {selectedWithdrawal.driver.name || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.phone")}>
                    {selectedWithdrawal.driver.phoneNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.email")}>
                    {selectedWithdrawal.driver.email || "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* Beneficiary Information */}
            {selectedWithdrawal.beneficiary && (
              <Card title={t("label.beneficiary.info")}>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label={t("label.beneficiary.id")}>
                    {selectedWithdrawal.beneficiary.id || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.name")}>
                    {selectedWithdrawal.beneficiary.name || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.beneficiary.iban")}>
                    {selectedWithdrawal.beneficiary.iban || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.beneficiary.bank")}>
                    {selectedWithdrawal.beneficiary.bank || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.beneficiary.bank_code")}>
                    {selectedWithdrawal.beneficiary.bankCode || "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </Space>
        )}
      </Modal>

      {/* Update Status Modal */}
      <Modal
        title={t("label.withdrawal.change_status")}
        open={isStatusModalVisible}
        onCancel={handleCloseStatusModal}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitStatus}
          initialValues={{ status: undefined }}
        >
          <Form.Item
            name="status"
            label={t("label.status")}
            rules={[
              { required: true, message: t("message.error.field_required") },
            ]}
          >
            <Select
              placeholder={t("label.status")}
              options={WITHDRAWAL_STATUS_OPTIONS.map((status) => ({
                value: status,
                label: t(`label.withdrawal.status.${status.toLowerCase()}`, { defaultValue: status }),
              }))}
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCloseStatusModal}>
                {t("button.cancel")}
              </Button>
              <Button type="primary" htmlType="submit" loading={updateLoading}>
                {t("button.save")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </RobinPage>
  );
}

export default WithdrawalsList;
